import {
  type BoardView,
  type Effort,
  type FileRef,
  type SessionEvent,
  type SessionMeta,
  type SessionSync,
  type TranscriptEntry,
  type Trigger,
} from '@shared/session'
import { type RevertRecord, type TurnChangeSet } from '@shared/trust'
import { readPref, writePref } from '@/lib/storage'
import { type BoardClient } from '@/board/client'

export type StreamPart = { turnId: string; name: string; input: Record<string, unknown> }

export type SessionsSnapshot = {
  sessions: SessionMeta[]
  /** Board message id -> sessions it was handed to. */
  fired: Record<string, string[]>
  current: string | null
  transcripts: Record<string, { entries: TranscriptEntry[]; queued: Trigger[] } | undefined>
  /** Streaming tool inputs of the turn in flight, by session then tool_use id. */
  partials: Record<string, Record<string, StreamPart> | undefined>
  status: Record<string, string | null | undefined>
  error: { sessionId: string | null; code: string; message: string } | null
  /** What each session's next turn would be told about the board (s.peek), by session. */
  pending: Record<string, { text: string | null; view: BoardView } | undefined>
  /** What a turn changed (s.changes), by turn id. */
  changes: Record<string, TurnChangeSet | undefined>
  /** Each session's reverts, by session id. */
  reverts: Record<string, RevertRecord[] | undefined>
  /** The session list has arrived at least once. */
  loaded: boolean
  /** The board's title, when the namer renamed it while this page was open. */
  boardTitle: string | null
  /**
   * A request to show a conversation (from the board: a message, a session
   * node), optionally at one board message. `n` changes on every request.
   */
  reveal: { sessionId: string; messageId: string | null; open: boolean; n: number } | null
  /**
   * Something to point at in the next chat message (a region of a material),
   * attached above the input. `n` changes on every request.
   */
  draft: { ref: DraftRef; n: number } | null
}

/** A place the next message points at: its label and where (`mat://…`, `item://…`). */
export type DraftRef = { label: string; href: string }

/**
 * The browser's view of a board's AI sessions. It mirrors what the board object
 * sends (shared/session.ts) and issues commands; it decides nothing itself.
 */
export class SessionsClient {
  private snap: SessionsSnapshot
  private listeners = new Set<() => void>()
  private readonly board: BoardClient
  private readonly prefKey: string
  private wantCreated = false
  private readonly off: () => void

  constructor(board: BoardClient, boardId: string) {
    this.board = board
    this.prefKey = `session:${boardId}`
    this.snap = {
      sessions: [],
      fired: {},
      current: readPref(this.prefKey),
      transcripts: {},
      partials: {},
      status: {},
      error: null,
      pending: {},
      changes: {},
      reverts: {},
      loaded: false,
      reveal: null,
      draft: null,
      boardTitle: null,
    }
    this.off = board.onSessionMessage((m) => {
      this.onMessage(m)
    })
    if (this.snap.current) board.sendSession({ t: 's.load', sessionId: this.snap.current })
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getSnapshot = () => this.snap

  private set(
    patch: Partial<SessionsSnapshot> | ((s: SessionsSnapshot) => Partial<SessionsSnapshot>),
  ) {
    const p = typeof patch === 'function' ? patch(this.snap) : patch
    this.snap = { ...this.snap, ...p }
    for (const fn of this.listeners) fn()
  }

  destroy() {
    this.off()
  }

  // ---------- commands ----------

  select(id: string | null) {
    writePref(this.prefKey, id)
    this.set({ current: id, error: null })
    if (id && !this.snap.transcripts[id]) this.board.sendSession({ t: 's.load', sessionId: id })
  }

  /**
   * Switch the chat to `sessionId` and scroll to `messageId` (a board message
   * it said or was handed). `open`: also open the chat if it is closed.
   */
  reveal(sessionId: string, messageId: string | null, open: boolean) {
    this.select(sessionId)
    this.set((s) => ({ reveal: { sessionId, messageId, open, n: (s.reveal?.n ?? 0) + 1 } }))
  }

  /** Point the next chat message at `ref`; the person finishes the message. */
  addToDraft(ref: DraftRef) {
    this.set((s) => ({ draft: { ref, n: (s.draft?.n ?? 0) + 1 } }))
  }

  create(opts: { model?: string; effort?: Effort } = {}) {
    this.wantCreated = true
    this.board.sendSession({ t: 's.create', ...opts })
  }

  /** Send chat text to the current session, creating one first if there is none. */
  send(text: string, files: FileRef[] = []) {
    const cur = this.snap.current
    if (!cur) {
      this.pendingSend = { text, files }
      this.create()
      return
    }
    this.board.sendSession({ t: 's.send', sessionId: cur, text, ...(files.length && { files }) })
  }

  private pendingSend: { text: string; files: FileRef[] } | null = null

  /** Hand board messages to a session (or a new one). */
  /** `sessionId`: a session, "new" for a fresh one, or null for the current one. */
  fire(sessionId: string | null, messageIds: string[]) {
    if (!messageIds.length) return
    const target = sessionId ?? this.snap.current ?? 'new'
    if (target === 'new') this.wantCreated = true
    this.board.sendSession({ t: 's.fire', sessionId: target, messageIds })
  }

  /** Ask what the session's next turn would be told about the board; the answer lands in `pending`. */
  /** Ask what a turn changed; `refs`: also who changed these afterwards. Lands in `changes`. */
  loadChanges(sessionId: string, turnId: string, refs: string[] = []) {
    this.board.sendSession({
      t: 's.changes',
      sessionId,
      turnId,
      ...(refs.length && { refs }),
    })
  }

  /** Put back what a turn changed, or one item of it. */
  revert(sessionId: string, turnId: string, itemId?: string) {
    this.board.sendSession({ t: 's.revert', sessionId, turnId, ...(itemId && { itemId }) })
  }

  unrevert(revertId: string) {
    this.board.sendSession({ t: 's.unrevert', revertId })
  }

  peek(sessionId: string) {
    this.board.sendSession({ t: 's.peek', sessionId })
  }

  stop(sessionId: string) {
    this.board.sendSession({ t: 's.stop', sessionId })
  }

  update(sessionId: string, patch: { title?: string; model?: string; effort?: Effort }) {
    this.board.sendSession({ t: 's.update', sessionId, ...patch })
  }

  dismissError() {
    this.set({ error: null })
  }

  // ---------- incoming ----------

  private onMessage(m: SessionEvent | SessionSync) {
    switch (m.t) {
      case 's.list': {
        const current =
          this.snap.current && m.sessions.some((s) => s.id === this.snap.current)
            ? this.snap.current
            : null
        this.set({ sessions: m.sessions, fired: m.fired, current, loaded: true })
        return
      }
      case 's.created': {
        if (!this.wantCreated) return
        this.wantCreated = false
        this.select(m.sessionId)
        if (this.pendingSend) {
          const { text, files } = this.pendingSend
          this.pendingSend = null
          this.send(text, files)
        }
        return
      }
      case 's.transcript':
        this.set((s) => ({
          transcripts: {
            ...s.transcripts,
            [m.sessionId]: { entries: m.entries, queued: m.queued },
          },
        }))
        return
      case 'entry':
        this.set((s) => {
          const cur = s.transcripts[m.sessionId]
          if (!cur) return {}
          const entries = cur.entries.some((e) => e.seq === m.entry.seq)
            ? cur.entries
            : [...cur.entries, m.entry]
          const out: Partial<SessionsSnapshot> = {
            transcripts: {
              ...s.transcripts,
              // A turn starting consumed the queue.
              [m.sessionId]: { entries, queued: m.entry.kind === 'turn_start' ? [] : cur.queued },
            },
          }
          // Streamed text is replaced by the stored message; a finished turn has no partials.
          if (m.entry.kind === 'assistant' || m.entry.kind === 'turn_end') {
            out.partials = { ...s.partials, [m.sessionId]: {} }
          }
          if (m.entry.kind === 'turn_end') out.status = { ...s.status, [m.sessionId]: null }
          return out
        })
        return
      case 'partial':
        this.set((s) => ({
          partials: {
            ...s.partials,
            [m.sessionId]: {
              ...s.partials[m.sessionId],
              [m.toolUseId]: { turnId: m.turnId, name: m.name, input: m.input },
            },
          },
        }))
        return
      case 'status':
        this.set((s) => ({ status: { ...s.status, [m.sessionId]: m.text } }))
        return
      case 's.changes':
        this.set((s) => ({ changes: { ...s.changes, [m.set.turnId]: m.set } }))
        return
      case 's.reverts':
        this.set((s) => ({ reverts: { ...s.reverts, [m.sessionId]: m.reverts } }))
        return
      case 'board.title':
        this.set({ boardTitle: m.title })
        return
      case 's.pending':
        this.set((s) => ({
          pending: { ...s.pending, [m.sessionId]: { text: m.text, view: m.view } },
        }))
        return
      case 's.error':
        this.set({ error: { sessionId: m.sessionId, code: m.code, message: m.message } })
        return
      case 'session':
        return
    }
  }
}
